<template>
  <div class="bg-white rounded-xl border border-gray-200 px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
    <MonthlyDateFilterSelector
      :key="periodSelectorKey"
      class="w-56"
      :placeholder="t('period_placeholder')"
      @newDateSelected="(startDate, endDate) => $emit('update-dates', startDate, endDate)"
    />
    <template v-for="filterGroup in filterGroups" :key="filterGroup.join()">
      <div class="h-6 w-px bg-gray-200"></div>
      <SearchBarFilter
        v-for="filterName in filterGroup"
        :key="filterName"
        :filterName="filterName"
        :filterIdx="FILTER_NAMES.indexOf(filterName)"
        :current-page="currentPage"
        :available-filters="availableFilters"
        :selected-filters="selectedFilters"
        :data-test="`filter-${filterName}`"
        @updateFilterSearch="(name, options) => $emit('update-filter', name, options)"
      />
    </template>
    <div class="ml-auto flex items-center gap-2">
      <ClearFiltersButton v-if="hasActiveFilters" class="mr-2" @click="clearFilters" />
      <label
        class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-full cursor-pointer select-none has-[:checked]:bg-red-50 has-[:checked]:border-red-300 has-[:checked]:text-red-800"
      >
        <input
          :checked="selectedFilters.withSubjectUnpaidBill"
          type="checkbox"
          data-test="with-subject-unpaid-bill-filter"
          class="accent-red-600"
          @change="$emit('update-unpaid-bill', $event.target.checked)"
        />
        {{ t("with_subject_unpaid_bill") }}
      </label>
      <VTooltip theme="info-tooltip">
        <InformationCircleIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
        <template #popper>
          <span>{{ t("with_subject_unpaid_bill_tooltip") }}</span>
        </template>
      </VTooltip>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { ClearFiltersButton, SearchBarFilter } from "@estaly/ui";
import { InformationCircleIcon } from "@heroicons/vue/outline";
import MonthlyDateFilterSelector from "@/components/menu/MonthlyDateFilterSelector.vue";

const FILTER_GROUPS = [
  ["organizations", "stores", "billingEntities"],
  ["transactionTypes", "paymentStatuses", "mandateStatuses"],
];
const FILTER_NAMES = FILTER_GROUPS.flat();

export default {
  name: "InvoicesFilterBar",

  components: { ClearFiltersButton, SearchBarFilter, MonthlyDateFilterSelector, InformationCircleIcon },

  props: {
    availableFilters: {
      type: Object,
      required: true,
    },
    selectedFilters: {
      type: Object,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
  },

  emits: ["update-filter", "update-dates", "update-unpaid-bill", "clear-filters"],

  setup() {
    const { t } = useI18n();
    return { t, FILTER_NAMES };
  },

  data() {
    return {
      periodSelectorKey: 0,
    };
  },

  computed: {
    filterGroups() {
      return FILTER_GROUPS.map((filterGroup) =>
        filterGroup.filter((filterName) => this.availableFilters[filterName]?.length >= 1),
      ).filter((filterGroup) => filterGroup.length > 0);
    },
    hasActiveFilters() {
      return (
        FILTER_NAMES.some((filterName) => this.selectedFilters[filterName]?.length > 0) ||
        Boolean(this.selectedFilters.withSubjectUnpaidBill || this.selectedFilters.paymentStartDate)
      );
    },
  },

  methods: {
    clearFilters() {
      this.periodSelectorKey += 1;
      this.$emit("clear-filters");
    },
  },
};
</script>

<i18n src="./InvoicesFilterBar.i18n.json"></i18n>
